import type {
    ClaimAnalysisResponseData,
    ClaimAnalysisResultInfo,
} from '../apis/oar-task-api';
import type {
    ClaimAnalysisResponse,
    ClaimAnalysisResult,
    ArgumentStrength,
} from '../types/claim-analysis';

/**
 * 映射论证强度字符串到枚举
 *
 * @param strengthStr - API 返回的论证强度字符串
 * @returns ArgumentStrength 枚举值
 */
function mapArgumentStrength(strengthStr: string): ArgumentStrength {
    const strengthMap: Record<string, ArgumentStrength> = {
        '强': '强' as ArgumentStrength,
        '中': '中' as ArgumentStrength,
        '弱': '弱' as ArgumentStrength,
    };

    return strengthMap[strengthStr] || '中' as ArgumentStrength;
}

/**
 * 映射单个权利要求分析结果
 *
 * 将 API 返回的权利要求分析结果转换为前端业务模型
 *
 * @param apiResult - API 返回的权利要求分析结果
 * @returns 前端业务模型的权利要求分析结果
 */
export function mapClaimAnalysisResult(
    apiResult: ClaimAnalysisResultInfo,
): ClaimAnalysisResult {
    return {
        claimInvolved: apiResult.claim_involved,
        examinerComparison: apiResult.examiner_comparison,
        verificationConclusion: apiResult.verification_conclusion,
        disclosedCommonKnowledgeNotDisclosed: apiResult.disclosed_common_knowledge_not_disclosed,
        argumentStrength: mapArgumentStrength(apiResult.argument_strength),
        recommendedAction: apiResult.recommended_action,
        argumentFeatures: apiResult.argument_features,
        strategyRecommendation: apiResult.strategy_recommendation,
        hasWarning: apiResult.has_warning,
        isStrongArgument: apiResult.is_strong_argument,
        isWeakArgument: apiResult.is_weak_argument,
    };
}

/**
 * 映射权利要求分析响应
 *
 * 将 API 返回的完整响应数据转换为前端业务模型
 *
 * @param apiResponse - API 返回的权利要求分析响应
 * @returns 前端业务模型的权利要求分析响应
 */
export function mapClaimAnalysisResponse(
    apiResponse: ClaimAnalysisResponseData,
): ClaimAnalysisResponse {
    return {
        taskId: apiResponse.task_id,
        conclusiveOpinionId: apiResponse.conclusive_opinion_id,
        conclusiveOpinionTitle: apiResponse.conclusive_opinion_title,
        results: apiResponse.results.map(mapClaimAnalysisResult),
        totalClaims: apiResponse.total_claims,
    };
}

// ==================== 反向映射（业务模型 → API 格式） ====================

/**
 * 反向映射权利要求分析结果（业务模型 → API 格式）
 *
 * 将前端业务模型转换为 API 接受的格式，用于调用 StatementGenerationService
 *
 * @param result - 前端业务模型的权利要求分析结果
 * @returns API 格式的权利要求分析结果
 *
 * @example
 * ```typescript
 * const apiFormatResult = toApiFormatResult(businessModelResult);
 * await statementService.generateStatement({
 *   taskId,
 *   claimAnalyses: [apiFormatResult],
 * });
 * ```
 */
export function toApiFormatResult(result: ClaimAnalysisResult): Record<string, any> {
    return {
        claim_involved: result.claimInvolved,
        examiner_comparison: result.examinerComparison,
        verification_conclusion: result.verificationConclusion,
        disclosed_common_knowledge_not_disclosed: result.disclosedCommonKnowledgeNotDisclosed,
        argument_strength: result.argumentStrength,
        recommended_action: result.recommendedAction,
        argument_features: result.argumentFeatures,
        strategy_recommendation: result.strategyRecommendation,
    };
}

/**
 * 批量反向映射权利要求分析结果
 *
 * @param results - 前端业务模型的权利要求分析结果数组
 * @returns API 格式的权利要求分析结果数组
 *
 * @example
 * ```typescript
 * const model = injectService<ClaimAnalysisResultModel>(ClaimAnalysisResultModel);
 * const currentResult = model.getCurrentResult();
 * if (currentResult) {
 *   const apiResults = toApiFormatResults(currentResult.results);
 *   await statementService.generateStatement({ taskId, claimAnalyses: apiResults });
 * }
 * ```
 */
export function toApiFormatResults(results: ClaimAnalysisResult[]): Record<string, any>[] {
    return results.map(toApiFormatResult);
}
